import React from "react";
import {
  IconButton,
  CircularProgress,
  Box,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { MicOnIcon, MicOffIcon } from "../../../assets/icon";
import {
  getCallMicDimensions,
  getRoleplayMicButtonSx,
  micButtonMutedStyles,
  micButtonStyles,
} from "../../live-speech-stage/styles";

interface AudioInputProps {
  muteInputAudio: () => void;
  unmuteInputAudio: () => void;
  isMuted: boolean;
  isVoiceChatLoading: boolean;
  isMicDisabledByWarning: boolean;
  isPushToTalk?: boolean;
  isPushPressed?: boolean;
  pushButtonRef?: React.Ref<HTMLButtonElement>;
  pushToTalkHandlers?: React.HTMLAttributes<HTMLButtonElement>;
}

export const AudioInput: React.FC<AudioInputProps> = ({
  muteInputAudio,
  unmuteInputAudio,
  isMuted,
  isVoiceChatLoading,
  isMicDisabledByWarning,
  isPushToTalk = false,
  isPushPressed = false,
  pushButtonRef,
  pushToTalkHandlers = {},
}) => {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const { buttonPx, iconPx } = getCallMicDimensions(isSmDown, isMdDown);

  const handleMuteClick = () => {
    if (isMuted) {
      unmuteInputAudio();
    } else {
      muteInputAudio();
    }
  };

  const showMicOn = isPushToTalk ? isPushPressed : !isMuted;
  const showMicOff = isPushToTalk ? !isPushPressed : isMuted;

  return (
    <Box
      sx={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
      }}
    >
      <IconButton
        ref={isPushToTalk ? pushButtonRef : undefined}
        onClick={isPushToTalk ? undefined : handleMuteClick}
        {...(isPushToTalk ? pushToTalkHandlers : {})}
        disabled={isVoiceChatLoading || isMicDisabledByWarning}
        sx={{
          ...(isPushToTalk
            ? getRoleplayMicButtonSx(isPushPressed, buttonPx)
            : showMicOn
              ? micButtonStyles
              : micButtonMutedStyles),
          ...(!isPushToTalk
            ? {
                width: buttonPx,
                height: buttonPx,
              }
            : {}),
          transition: "transform 0.15s ease",
          transform:
            isPushToTalk && isPushPressed ? "scale(1.333)" : "scale(1)",
          transformOrigin: "center center",
          touchAction: "none",
          zIndex: isPushToTalk && isPushPressed ? 20 : undefined,
        }}
        title={
          isPushToTalk
            ? "Push and hold to talk"
            : isMuted
              ? "Unmute"
              : "Mute"
        }
      >
        {isVoiceChatLoading ? (
          <CircularProgress size={iconPx} sx={{ color: "currentColor" }} />
        ) : showMicOff ? (
          <MicOffIcon
            style={{
              width: iconPx,
              height: iconPx,
              color: "currentColor",
            }}
          />
        ) : (
          <MicOnIcon
            style={{
              width: iconPx,
              height: iconPx,
              color: "currentColor",
            }}
          />
        )}
      </IconButton>
    </Box>
  );
};
